import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Active } from 'types';
import { getProductDisplayName } from '../../../../mappers/serviceAgreementToTreeMapper';
import { DEFAULT_SAVE_STATE, getSaveGate, SaveState } from '../../../utils/saveGate';
import { ClosureOnSaveConfirmation } from '../../components/ClosureOnSaveConfirmation';
import { CloseProductSection } from '../../components/CloseProductSection';
import { SafeDepositBoxServiceRoleSection } from '../../components/SafeDepositBoxServiceRoleSection';
import { useCloseConfigureModal } from '../../hooks/useCloseConfigureModal';
import { useCloseServiceAgreement } from '../../hooks/useCloseServiceAgreement';
import { useClosureConfirmingSave } from '../../hooks/useClosureConfirmingSave';
import { useGenericCloseBodyText } from '../../hooks/useGenericCloseBodyText';
import { ConfigureProductDialog } from '../../shell/ConfigureProductDialog';
import { isTerminalSafeDepositBoxStatus } from '../../utils/safeDepositBoxStatus';
import type { ConfigureProductGroupModalProps } from '../configureProductGroupModalProps';

const MODAL_WIDTH = '1080px';

export const SafeDepositBoxModal: React.FC<ConfigureProductGroupModalProps> = ({
  serviceAgreement,
  brUid,
  relatedServiceAgreementNames,
}) => {
  const { t } = useTranslation();
  const [saveState, setSaveState] = useState<SaveState>(DEFAULT_SAVE_STATE);
  const { close, isClosing } = useCloseServiceAgreement(serviceAgreement, brUid);
  const closeBodyText = useGenericCloseBodyText();
  const { canSave, reasons: saveBlockedReasons } = getSaveGate([saveState.blocker]);
  const { isPending, save } = saveState;
  const handleClose = useCloseConfigureModal(isPending);

  const { handleSave, isConfirmingClosure, confirmClosure, cancelClosure } = useClosureConfirmingSave({
    saveState,
    canSave,
    confirm: async () => (save ? save() : undefined),
  });

  const showCloseProductSection = !isTerminalSafeDepositBoxStatus(serviceAgreement.agreementStatus);
  const disabledCloseReason =
    serviceAgreement.agreementStatus === Active.ACTIVE
      ? t('pb.configureproductmodal.safedepositbox.active.close.tooltip')
      : null;
  const displayName = getProductDisplayName(serviceAgreement);
  const productNames = [displayName, ...relatedServiceAgreementNames].filter((name): name is string => Boolean(name));

  return (
    <>
      <ConfigureProductDialog
        title={t('pb.configureproductmodal.title', 'Edit product')}
        width={MODAL_WIDTH}
        size="large"
        isPending={isPending}
        onClose={handleClose}
        footer={{ canSave, saveBlockedReasons, onSave: handleSave }}
        editor={
          <SafeDepositBoxServiceRoleSection
            serviceAgreement={serviceAgreement}
            brUid={brUid}
            onSaveStateChange={setSaveState}
          />
        }
      >
        {showCloseProductSection && (
          <CloseProductSection
            isPending={isPending}
            bodyText={closeBodyText}
            addTopMargin
            disabledReason={disabledCloseReason}
            isClosing={isClosing}
            onConfirmClose={close}
          />
        )}
      </ConfigureProductDialog>
      {isConfirmingClosure && (
        <ClosureOnSaveConfirmation
          productNames={productNames}
          isPending={isPending}
          onConfirm={confirmClosure}
          onCancel={cancelClosure}
        />
      )}
    </>
  );
};
